import React, { useState } from "react";
import filesize from "filesize";
import { useContextSelector } from "use-context-selector";

import { Text } from "@xometry/xometry_loft";

import { PfsFile } from "../../schema/generated/schema";

import fileUtils, { FileShareEntityType } from "../../utils/fileUtils";

import { JsonSchemaDataContext } from "../JsonSchemaLoftTheme/JsonSchemaDataContext";

import FileUnitField from "./FileUnitField";
import {
  DeleteButton,
  FileDownloadLink,
  FileSize,
  ListItem,
  PartDimensions,
  Row,
  StyledList,
  View3DButton,
} from "./FileList.style";
import FileContext from "../proposals/contexts/fileContext";
import UserContext from "../proposals/contexts/userContext";
import Modals from "../proposals/Modals";
import { useQueryParams } from "../../utils/routerUtils";

const FileList = ({
  disabled,
  disableLink,
}: {
  disabled: boolean;
  disableLink?: boolean;
}) => {
  const { guest } = useQueryParams();
  const sellerPartnerId = useContextSelector(
    JsonSchemaDataContext,
    context => context.valueModel?.sellerPartnerId
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const files = useContextSelector(FileContext, ctx => ctx.data.files);
  const unshareFile = useContextSelector(
    FileContext,
    ctx => ctx.actions.unshareFile
  );
  const removeFile = useContextSelector(
    FileContext,
    ctx => ctx.actions.removeFile
  );
  const loggedInUserId = useContextSelector(
    UserContext,
    ctx => ctx.loggedInUser?.id
  );

  const [isRemoveFileModalOpen, setRemoveFileModalOpen] = useState(false);

  const [pfsFile, setPfsFile] = useState<PfsFile | null>(null);

  const handleDelete = async () => {
    if (isRemoving) {
      return;
    }
    setIsRemoving(true);
    try {
      if (pfsFile) {
        const { entityType, entityId } = pfsFile.contextMeta;
        if (
          entityType === FileShareEntityType.OPPORTUNITY.toString() &&
          sellerPartnerId
        ) {
          await unshareFile(pfsFile.id, sellerPartnerId);
        } else {
          await removeFile(entityId, entityType, pfsFile.id);
        }
      }
    } catch (err) {
      console.error(err);
    }
    setPfsFile(null);
    setIsRemoving(false);
  };
  return (
    <StyledList>
      {files.map(pfsFile => {
        const isOwnedByLoggedInUser =
          pfsFile.owner?.externalId === loggedInUserId;
        return (
          <ListItem key={pfsFile.id}>
            <Row>
              <FileDownloadLink
                file={pfsFile}
                disableLink={disableLink ?? false}
              />
              {pfsFile.geometry?.boundingBox && (
                <PartDimensions alignItems="center">
                  <Text kind="body-sm" marginRight={2}>
                    {fileUtils.formatPartDimensions(
                      pfsFile.geometry.boundingBox,
                      pfsFile.geometry.unit
                    )}
                  </Text>
                  {!disabled &&
                    isOwnedByLoggedInUser &&
                    fileUtils.fileNeedUserInputForUnits(pfsFile) && (
                      <FileUnitField file={pfsFile} />
                    )}
                </PartDimensions>
              )}
              <FileSize>{filesize(fileUtils.getFileSize(pfsFile))}</FileSize>
              {!guest && (
                <View3DButton file={pfsFile}>
                  <i className="far fa-cube" />
                  &nbsp; View 3D
                </View3DButton>
              )}
              {!disabled && isOwnedByLoggedInUser && (
                <>
                  <DeleteButton
                    disabled={isRemoving}
                    kind="text"
                    onClick={() => {
                      setPfsFile(pfsFile);
                      setRemoveFileModalOpen(true);
                    }}
                  >
                    <i className="far fa-trash-alt" />
                  </DeleteButton>
                  <Modals.DeleteFileModal
                    modalOpen={isRemoveFileModalOpen}
                    setModal={setRemoveFileModalOpen}
                    isDeleting={isRemoving}
                    onSubmit={() => {
                      handleDelete();
                    }}
                  />
                </>
              )}
            </Row>
          </ListItem>
        );
      })}
    </StyledList>
  );
};

FileList.defaultProps = { disableLink: false };

export default FileList;
